import { useMemo, useCallback } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { DataTable } from "@/components/shared/DataTable";

const UNITS = ["SF", "LF", "EA", "HR", "LS"];

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export interface COLineItem {
  id: string;
  line_number: number;
  description: string;
  quantity: number;
  unit: string;
  labor_unit_price: number;
  material_unit_price: number;
  unit_price: number;
}

interface Props {
  lines: COLineItem[];
  onChange: (lines: COLineItem[]) => void;
  disabled?: boolean;
}

export const COLineItemsForm = ({ lines, onChange, disabled }: Props) => {
  const addLine = () => {
    const next: COLineItem = {
      id: crypto.randomUUID(),
      line_number: lines.length + 1,
      description: "",
      quantity: 1,
      unit: "SF",
      labor_unit_price: 0,
      material_unit_price: 0,
      unit_price: 0,
    };
    onChange([...lines, next]);
  };

  const updateLine = useCallback(
    (id: string, field: keyof COLineItem, value: string | number) => {
      onChange(
        lines.map((l) => {
          if (l.id !== id) return l;
          const updated = { ...l, [field]: value };
          if (field === "labor_unit_price" || field === "material_unit_price") {
            updated.unit_price = Number(updated.labor_unit_price) + Number(updated.material_unit_price);
          }
          return updated;
        })
      );
    },
    [lines, onChange]
  );

  const removeLine = (id: string) => {
    onChange(
      lines
        .filter((l) => l.id !== id)
        .map((l, i) => ({ ...l, line_number: i + 1 }))
    );
  };

  const total = lines.reduce((sum, l) => sum + l.quantity * l.unit_price, 0);

  const columns = useMemo<ColumnDef<COLineItem, any>[]>(
    () => [
      {
        accessorKey: "line_number",
        header: "#",
        size: 50,
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.line_number}</span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 200,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            value={row.original.description}
            onChange={(e) => updateLine(row.original.id, "description", e.target.value)}
            placeholder="Scope description"
            className="h-8 text-sm"
          />
        ),
      },
      {
        accessorKey: "quantity",
        header: "Qty",
        size: 80,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            type="number"
            min={0}
            step="0.01"
            value={row.original.quantity}
            onChange={(e) => updateLine(row.original.id, "quantity", Number(e.target.value))}
            className="h-8 text-sm text-right"
          />
        ),
      },
      {
        accessorKey: "unit",
        header: "Unit",
        size: 80,
        cell: ({ row }) => (
          <Select
            disabled={disabled}
            value={row.original.unit}
            onValueChange={(v) => updateLine(row.original.id, "unit", v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        accessorKey: "labor_unit_price",
        header: "Labor Rate",
        size: 100,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            type="number"
            min={0}
            step="0.01"
            value={row.original.labor_unit_price}
            onChange={(e) => updateLine(row.original.id, "labor_unit_price", Number(e.target.value))}
            className="h-8 text-sm text-right"
          />
        ),
      },
      {
        accessorKey: "material_unit_price",
        header: "Material Rate",
        size: 100,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            type="number"
            min={0}
            step="0.01"
            value={row.original.material_unit_price}
            onChange={(e) => updateLine(row.original.id, "material_unit_price", Number(e.target.value))}
            className="h-8 text-sm text-right"
          />
        ),
      },
      {
        accessorKey: "unit_price",
        header: "Unit Price",
        size: 100,
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-right block font-mono text-sm">
            {fmt(row.original.unit_price)}
          </span>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        size: 110,
        accessorFn: (row) => row.quantity * row.unit_price,
        cell: ({ row }) => (
          <span className="text-right block font-mono text-sm font-medium">
            {fmt(row.original.quantity * row.original.unit_price)}
          </span>
        ),
      },
      ...(!disabled
        ? [
            {
              id: "actions",
              header: "",
              size: 40,
              enableSorting: false,
              cell: ({ row }: any) => (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeLine(row.original.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ),
            } as ColumnDef<COLineItem, any>,
          ]
        : []),
    ],
    [disabled, updateLine, removeLine]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Line Items</h4>
        {!disabled && (
          <Button type="button" size="sm" variant="outline" onClick={addLine}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Line
          </Button>
        )}
      </div>

      {lines.length === 0 ? (
        <div className="text-center py-6 border rounded-md bg-muted/20">
          <p className="text-sm text-muted-foreground mb-2">No line items yet</p>
          {!disabled && (
            <Button type="button" size="sm" variant="outline" onClick={addLine}>
              Add your first line item
            </Button>
          )}
        </div>
      ) : (
        <div>
          <DataTable<COLineItem>
            tableId="co-line-items"
            columns={columns}
            data={lines}
            enableFiltering={false}
            enableColumnToggle={false}
            enableSorting={true}
            enableResize={true}
            enableReorder={true}
            emptyMessage="No line items"
          />
          <div className="flex justify-between items-center px-4 py-2 bg-muted/30 border border-t-0 rounded-b-md">
            <span className="text-sm font-semibold">Total</span>
            <span className="text-lg font-bold font-mono">{fmt(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};
